import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

function Stats() {
  const [summary, setSummary] = useState(null);
  const [levels, setLevels] = useState(null);
  const [categories, setCategories] = useState([]);
  const [daily, setDaily] = useState([]);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [summaryRes, levelsRes, categoriesRes, dailyRes, streakRes] = await Promise.all([
        axios.get(`${API}/stats/summary`),
        axios.get(`${API}/stats/levels`),
        axios.get(`${API}/stats/categories`),
        axios.get(`${API}/stats/daily`),
        axios.get(`${API}/stats/streak`),
      ]);
      setSummary(summaryRes.data);
      setLevels(levelsRes.data);
      setCategories(categoriesRes.data);
      setDaily(dailyRes.data);
      setStreak(streakRes.data.streak);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const levelNames = { 1: 'Neu', 2: 'Anfänger', 3: 'Fortgeschritten', 4: 'Gut', 5: 'Gelernt' };
  const levelColors = { 1: '#fc8181', 2: '#f6ad55', 3: '#f6e05e', 4: '#68d391', 5: '#63b3ed' };

  const maxDaily = daily.length > 0 ? Math.max(...daily.map(d => parseInt(d.total))) : 1;

  // Letzte 14 Tage aufbereiten
  const last14 = [];
  for (let i = 13; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const found = daily.find(d => d.date && d.date.startsWith(dateStr));
    last14.push({
      date: dateStr,
      total: found ? parseInt(found.total) : 0,
      correct: found ? parseInt(found.correct) : 0,
      label: date.toLocaleDateString('de-CH', { weekday: 'short' }),
    });
  }

  if (loading) return <div className="card"><p>Lade Statistiken...</p></div>;

  return (
    <div>
      <h1>📊 Auswertungen</h1>

      {/* Gesamtübersicht */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#4c51bf' }}>
            {summary?.totalCards || 0}
          </div>
          <div style={{ color: '#718096', fontSize: '0.9rem', marginTop: '0.25rem' }}>Karten total</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#e53e3e' }}>
            {summary?.dueCards || 0}
          </div>
          <div style={{ color: '#718096', fontSize: '0.9rem', marginTop: '0.25rem' }}>Fällig heute</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#38a169' }}>
            {streak} 🔥
          </div>
          <div style={{ color: '#718096', fontSize: '0.9rem', marginTop: '0.25rem' }}>Tage Streak</div>
        </div>
      </div>

      {/* Stufen-Übersicht */}
      {levels && (
        <div className="card">
          <h2>Stufen-Übersicht</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            {[1, 2, 3, 4, 5].map(level => {
              const count = levels[level] || 0;
              const total = Object.values(levels).reduce((a, b) => a + b, 0);
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <div key={level}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                      Stufe {level} — {levelNames[level]}
                    </span>
                    <span style={{ fontSize: '0.9rem', color: '#718096' }}>{count} Karten ({pct}%)</span>
                  </div>
                  <div style={{ background: '#e2e8f0', borderRadius: '999px', height: '12px' }}>
                    <div style={{
                      width: `${pct}%`, height: '12px', borderRadius: '999px',
                      background: levelColors[level], transition: 'width 0.5s ease'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tägliche Lernstatistik */}
      <div className="card">
        <h2>Letzte 14 Tage</h2>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '120px', marginTop: '1rem' }}>
          {last14.map((day, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', width: '100%' }}>
                <div style={{
                  width: '100%',
                  height: maxDaily > 0 ? `${(day.total / maxDaily) * 100}%` : '0%',
                  background: day.total > 0 ? '#4c51bf' : '#e2e8f0',
                  borderRadius: '4px 4px 0 0',
                  minHeight: '4px',
                  transition: 'height 0.3s ease',
                  title: `${day.total} gelernt`
                }} />
              </div>
              <div style={{ fontSize: '0.6rem', color: '#718096', marginTop: '4px' }}>{day.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', fontSize: '0.85rem' }}>
          <span>📚 Total gelernt: <strong>{daily.reduce((a, d) => a + parseInt(d.total), 0)}</strong></span>
          <span>✓ Richtig: <strong>{daily.reduce((a, d) => a + parseInt(d.correct), 0)}</strong></span>
        </div>
      </div>

      {/* Lernfortschritt pro Kategorie */}
      <div className="card">
        <h2>Fortschritt pro Kategorie</h2>
        {categories.length === 0 && (
          <p style={{ color: '#718096', marginTop: '0.5rem' }}>Noch keine Kategorien vorhanden.</p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
          {categories.map(cat => {
            const total = parseInt(cat.total) || 0;
            const learned = parseInt(cat.learned) || 0;
            const pct = total > 0 ? Math.round((learned / total) * 100) : 0;
            return (
              <div key={cat.name}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{cat.name}</span>
                  <span style={{ fontSize: '0.85rem', color: '#718096' }}>
                    {learned}/{total} gelernt · Ø Stufe {cat.avg_level}
                  </span>
                </div>
                <div style={{ background: '#e2e8f0', borderRadius: '999px', height: '10px' }}>
                  <div style={{
                    width: `${pct}%`, height: '10px', borderRadius: '999px',
                    background: pct >= 80 ? '#48bb78' : pct >= 40 ? '#f6ad55' : '#fc8181',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Stats;